import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Spinner,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Checkbox,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd, MdOutlineFilterAltOff, MdVisibility } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateNews from "./CreateNews";
import NewsMediaManager from "./NewsMediaManager";
import ViewNewsModal from "./ViewNewsModal";
import { validateNewsForm, findFirstErrorLocaleIndex, summarizeFormErrors, MIN_TEXT_WORDS, stripHtml } from "./newsValidation";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { toLocalTime } from "utils/time";
import { getTranslatedField, translationsArrayToMap, useLocales, REQUIRED_LOCALE_CODE } from "utils/translations";
import RichTextEditor from "components/richTextEditor/RichTextEditor";
import ConfirmDialog from "components/confirm/ConfirmDialog";

const NEWS_PAGE_ID = PAGE_IDS.NEWS;
const TAGS_PAGE_ID = PAGE_IDS.TAGS;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyTranslation = () => ({ title: "", header: "", text: "", slug: "", metaTitle: "", metaDesc: "" });

const STATUS_COLORS = { DRAFT: "gray", PUBLISHED: "green", ARCHIVED: "orange" };

const ListNews = () => {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });

  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const createFormRef = useRef(null);
  const [editableNews, setEditableNews] = useState(null);
  const [viewingNews, setViewingNews] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [editActiveTabIndex, setEditActiveTabIndex] = useState(0);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterSubcategoryId, setFilterSubcategoryId] = useState("");
  const [filterBreakingNews, setFilterBreakingNews] = useState("");

  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === NEWS_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoriesRes, subcategoriesRes, tagsRes] = await Promise.all([
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`),
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/sub/category`),
          fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/tag?page_id=${TAGS_PAGE_ID}`),
        ]);
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
        setSubcategories(Array.isArray(subcategoriesRes) ? subcategoriesRes : []);
        setTagOptions(tagsRes?.data || []);
      } catch (err) {
        console.error("Error fetching category/subcategory/tag options:", err);
      }
    };
    fetchOptions();
  }, []);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: perPage });
      if (filterCategoryId) params.append("category_id", filterCategoryId);
      if (filterSubcategoryId) params.append("subcategory_id", filterSubcategoryId);
      if (filterBreakingNews) params.append("breaking_news", filterBreakingNews);

      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/news?${params.toString()}`);
      if (response) {
        setNews(response.data || []);
        if (response.meta) {
          setTotalPages(response.meta.last_page || 1);
        }
      }
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch news."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, filterCategoryId, filterSubcategoryId, filterBreakingNews, t, toast]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleCreated = () => {
    setShowCreateModal(false);
    setCurrentPage(1);
    fetchNews();
  };

  const buildEditableState = (item) => ({
    ...item,
    category_id: item.category_id || item.category?.id || "",
    subcategory_id: item.subcategory_id || item.subcategory?.id || "",
    // Snapshot of what subcategory_id was when editing started, so handleSave
    // can tell "left alone" apart from "explicitly changed" and omit the
    // field entirely in the former case (PUT is a partial update).
    _originalSubcategoryId: item.subcategory_id || item.subcategory?.id || null,
    translations: translationsArrayToMap(item.translations, emptyTranslation, locales),
    selectedTagIds: (item.tags || []).map((tg) => tg.id),
    publisherBy: item.publisher_by || "",
    status: item.status || "DRAFT",
  });

  const handleEdit = (item) => {
    setFieldErrors({});
    setEditActiveTabIndex(0);
    setEditableNews(buildEditableState(item));
  };

  const handleEditClose = () => {
    setEditableNews(null);
    setFieldErrors({});
  };

  // `prev.translations[locale]` can briefly be missing for a locale that
  // just became active — fall back to a full empty shape instead of
  // spreading undefined, so no field silently drops out.
  const setTranslationField = (locale, field, value) => {
    setEditableNews((prev) => ({
      ...prev,
      translations: { ...prev.translations, [locale]: { ...emptyTranslation(), ...prev.translations[locale], [field]: value } },
    }));
  };

  const toggleTag = (tagId) => {
    setEditableNews((prev) => ({
      ...prev,
      selectedTagIds: prev.selectedTagIds.includes(tagId)
        ? prev.selectedTagIds.filter((id) => id !== tagId)
        : [...prev.selectedTagIds, tagId],
    }));
  };

  // Re-fetch just this one item (keeps its newsimages/newsvideos current)
  // after the media manager adds/removes a file.
  const refreshEditableNews = async () => {
    if (!editableNews) return;
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/news/${editableNews.id}`);
      const updated = response?.data || response;
      if (updated) {
        setEditableNews(buildEditableState(updated));
        setNews((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      }
    } catch (error) {
      console.error("Error refreshing news item:", error);
    }
  };

  const handleSave = async () => {
    const errors = validateNewsForm(
      {
        categoryId: editableNews.category_id,
        translations: editableNews.translations,
        status: editableNews.status,
        publisherBy: editableNews.publisherBy,
        locales,
        source: editableNews.source,
      },
      t
    );
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const errorTabIndex = findFirstErrorLocaleIndex(errors, locales);
      if (errorTabIndex !== -1) setEditActiveTabIndex(errorTabIndex);
      toast({
        title: t("Please fix the highlighted fields."),
        description: summarizeFormErrors(errors, locales, t),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsSaving(true);
    try {
      const translationsPayload = [];
      locales.forEach(({ code }) => {
        const tr = editableNews.translations[code] || emptyTranslation();
        if (!tr.title.trim() && !stripHtml(tr.text).trim()) return;
        translationsPayload.push({
          locale: code,
          title: tr.title.trim(),
          ...(tr.header.trim() && { header: tr.header.trim() }),
          text: tr.text,
          ...(tr.slug.trim() && { slug: tr.slug.trim() }),
          ...(tr.metaTitle.trim() && { metaTitle: tr.metaTitle.trim() }),
          ...(tr.metaDesc.trim() && { metaDesc: tr.metaDesc.trim() }),
        });
      });

      const currentSubcategoryId = editableNews.subcategory_id ? Number(editableNews.subcategory_id) : null;
      const subcategoryChanged = currentSubcategoryId !== (editableNews._originalSubcategoryId || null);
      const heroRankValue =
        editableNews.heroRank === "" || editableNews.heroRank === undefined || editableNews.heroRank === null
          ? null
          : Number(editableNews.heroRank);

      const data = {
        page_id: NEWS_PAGE_ID,
        news_id: editableNews.id,
        category_id: Number(editableNews.category_id),
        ...(subcategoryChanged && { subcategory_id: currentSubcategoryId }),
        priority: !!editableNews.priority,
        heroRank: heroRankValue,
        breaking_news: !!editableNews.breaking_news,
        status: editableNews.status,
        ...(editableNews.status === "PUBLISHED" ? { publisher_by: editableNews.publisherBy.trim() } : {}),
        translations: translationsPayload,
        tags: editableNews.selectedTagIds,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/news`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update news");
      }
      await fetchNews();
      setEditableNews(null);
      toast({
        title: t("News updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update news."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    const newsId = deleteTargetId;
    setIsDeleting(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/news`,
        "DELETE",
        { page_id: NEWS_PAGE_ID, news_id: newsId }
      );
      if (!response) {
        throw new Error("Failed to delete news");
      }
      setNews((prev) => prev.filter((n) => n.id !== newsId));
      setDeleteTargetId(null);
      toast({
        title: t("News deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to delete news."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const editSubcategoryOptions = subcategories.filter(
    (s) => !editableNews?.category_id || s.categoryId === Number(editableNews.category_id)
  );

  const renderTranslationTab = (code) => {
    if (!editableNews) return null;
    const tr = editableNews.translations[code] || emptyTranslation();
    const localeErrors = fieldErrors[code] || {};
    const wordCount = stripHtml(tr.text).trim().split(/\s+/).filter(Boolean).length;
    const isRtl = code === "ar";

    return (
      <Box>
        <FormControl isInvalid={!!localeErrors.title} mt="8px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Title")}</FormLabel>
          <Input dir={isRtl ? "rtl" : "ltr"} value={tr.title} onChange={(e) => setTranslationField(code, "title", e.target.value)} {...fieldProps} />
          <FormErrorMessage>{localeErrors.title}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!localeErrors.header} mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Header")}</FormLabel>
          <Input dir={isRtl ? "rtl" : "ltr"} value={tr.header} onChange={(e) => setTranslationField(code, "header", e.target.value)} {...fieldProps} />
          <FormErrorMessage>{localeErrors.header}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!localeErrors.text} mt="16px">
          <Flex justify="space-between" align="center">
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="0">{t("Text")}</FormLabel>
            <Text fontSize="xs" color={wordCount < MIN_TEXT_WORDS ? "red.500" : "gray.500"}>
              {t("{{count}} words (minimum {{min}})", { count: wordCount, min: MIN_TEXT_WORDS })}
            </Text>
          </Flex>
          <RichTextEditor
            dir={isRtl ? "rtl" : "ltr"}
            value={tr.text}
            onChange={(html) => setTranslationField(code, "text", html)}
            isInvalid={!!localeErrors.text}
          />
          <FormErrorMessage>{localeErrors.text}</FormErrorMessage>
        </FormControl>

        <FormControl mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Slug")}</FormLabel>
          <Input value={tr.slug} onChange={(e) => setTranslationField(code, "slug", e.target.value)} {...fieldProps} />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Meta Title (SEO)")}</FormLabel>
            <Input value={tr.metaTitle} onChange={(e) => setTranslationField(code, "metaTitle", e.target.value)} {...fieldProps} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Meta Description (SEO)")}</FormLabel>
            <Input value={tr.metaDesc} onChange={(e) => setTranslationField(code, "metaDesc", e.target.value)} {...fieldProps} />
          </FormControl>
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap" gap={3}>
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("News")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add News")}
          </Button>
        </Flex>

        <Flex align="center" gap={3} flexWrap="wrap">
          <Select
            size="sm"
            w="160px"
            placeholder={t("All Categories")}
            value={filterCategoryId}
            onChange={(e) => { setFilterCategoryId(e.target.value); setFilterSubcategoryId(""); setCurrentPage(1); }}
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{getTranslatedField(c.translations, "en")}</option>
            ))}
          </Select>
          <Select
            size="sm"
            w="160px"
            placeholder={t("All Sub Categories")}
            value={filterSubcategoryId}
            onChange={(e) => { setFilterSubcategoryId(e.target.value); setCurrentPage(1); }}
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
          >
            {subcategories
              .filter((s) => !filterCategoryId || s.categoryId === Number(filterCategoryId))
              .map((s) => (
                <option key={s.id} value={s.id}>{getTranslatedField(s.translations, "en")}</option>
              ))}
          </Select>
          <Select
            size="sm"
            w="140px"
            placeholder={t("Breaking?")}
            value={filterBreakingNews}
            onChange={(e) => { setFilterBreakingNews(e.target.value); setCurrentPage(1); }}
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
          >
            <option value="true">{t("Breaking Only")}</option>
            <option value="false">{t("Not Breaking")}</option>
          </Select>
          <Button
            size="sm"
            leftIcon={<MdOutlineFilterAltOff />}
            variant="outline"
            colorScheme="red"
            onClick={() => {
              setFilterCategoryId("");
              setFilterSubcategoryId("");
              setFilterBreakingNews("");
              setCurrentPage(1);
            }}
          >
            {t("Reset")}
          </Button>
        </Flex>
      </Flex>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Title")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Category")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Sub Category")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Breaking")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Created")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {news.map((item) => (
                <Tr key={item.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td maxW="240px">
                    <Flex align="center" gap={2}>
                      <Text noOfLines={1}>{getTranslatedField(item.translations, "en", "title") || "—"}</Text>
                      {item.source === "telegram" && (
                        <Badge flexShrink={0} bg="#229ED9" color="white" px={2} py={1} borderRadius="md" fontSize="0.7em">
                          {t("Telegram")}
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td>{getTranslatedField(item.category?.translations, "en") || "—"}</Td>
                  <Td>{getTranslatedField(item.subcategory?.translations, "en") || "—"}</Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS[item.status] || "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                      {t(item.status || "DRAFT")}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={item.breaking_news ? "red" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                      {item.breaking_news ? t("Breaking") : t("No")}
                    </Badge>
                  </Td>
                  <Td>{item.createdAt ? toLocalTime(item.createdAt) : "—"}</Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor="gray.200"
                        color="gray.700"
                        _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
                        size="sm"
                        mr="2"
                        onClick={() => setViewingNews(item)}
                        leftIcon={<MdVisibility />}
                      >
                        {t("View")}
                      </Button>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(item)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => setDeleteTargetId(item.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {totalPages > 0 && (
        <Flex justifyContent="center" wrap="wrap" gap={2} mt={4} mb={4}>
          <Button
            size="sm"
            variant="outline"
            borderColor="gray.200"
            color="gray.700"
            borderRadius="full"
            _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            {t("Previous")}
          </Button>
          <Text alignSelf="center" fontSize="sm" color="gray.600">
            {t("Page")} {currentPage} {t("of")} {totalPages}
          </Text>
          <Button
            size="sm"
            variant="outline"
            borderColor="gray.200"
            color="gray.700"
            borderRadius="full"
            _hover={{ borderColor: GOLD_TEXT, color: GOLD_TEXT }}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            {t("Next")}
          </Button>
        </Flex>
      )}

      {/* Create News Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create News")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <CreateNews ref={createFormRef} onCreated={handleCreated} onSubmittingChange={setIsCreating} />
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
            <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setShowCreateModal(false)}>
              {t("Cancel")}
            </Button>
            <Button
              bg={GOLD_BORDER}
              color="#1A1A1A"
              _hover={{ bg: GOLD_TEXT, color: "white" }}
              _active={{ bg: GOLD_TEXT, color: "white" }}
              onClick={() => createFormRef.current?.submit()}
              isLoading={isCreating}
            >
              {t("Create News")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit News Modal */}
      {editableNews && (
        <Modal isOpen={true} onClose={handleEditClose} size={modalSize} scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="85vh">
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Flex align="center" gap={2}>
                <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                  {t("Edit News")}
                </Text>
                {editableNews.source === "telegram" && (
                  <Badge bg="#229ED9" color="white" fontSize="0.6em">{t("Via Telegram")}</Badge>
                )}
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isInvalid={!!fieldErrors.categoryId}>
                  <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Category")}</FormLabel>
                  <Select
                    placeholder={t("Select a category")}
                    value={editableNews.category_id}
                    onChange={(e) => setEditableNews({ ...editableNews, category_id: e.target.value, subcategory_id: "" })}
                    {...fieldProps}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{getTranslatedField(c.translations, "en")}</option>
                    ))}
                  </Select>
                  <FormErrorMessage>{fieldErrors.categoryId}</FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Sub Category")}</FormLabel>
                  <Select
                    placeholder={t("Select a sub category (optional)")}
                    value={editableNews.subcategory_id || ""}
                    onChange={(e) => setEditableNews({ ...editableNews, subcategory_id: e.target.value })}
                    {...fieldProps}
                  >
                    {editSubcategoryOptions.map((s) => (
                      <option key={s.id} value={s.id}>{getTranslatedField(s.translations, "en")}</option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <Tabs mt="16px" colorScheme="gray" variant="enclosed" index={editActiveTabIndex} onChange={setEditActiveTabIndex}>
                <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap" sx={{ scrollbarWidth: "thin" }}>
                  {locales.map(({ code, label }) => (
                    <Tab key={code} whiteSpace="nowrap" flexShrink={0} _selected={{ color: GOLD_TEXT, borderColor: "gray.200", borderBottomColor: "white", fontWeight: "700" }}>
                      {label}
                      {code === REQUIRED_LOCALE_CODE && <Text as="span" color="red.500" ml="4px">*</Text>}
                    </Tab>
                  ))}
                </TabList>
                <TabPanels borderWidth="1px" borderTop="none" borderColor="gray.200" borderBottomRadius="md" p="12px">
                  {locales.map(({ code }) => (
                    <TabPanel key={code} p="4px">
                      {renderTranslationTab(code)}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>

              <Box mt="16px">
                <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Tags")}</FormLabel>
                {tagOptions.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">{t("No tags yet — create one from the Tags page.")}</Text>
                ) : (
                  <Wrap>
                    {tagOptions.map((tagOption) => {
                      const isSelected = editableNews.selectedTagIds.includes(tagOption.id);
                      return (
                        <WrapItem key={tagOption.id}>
                          <Tag
                            as="button"
                            type="button"
                            onClick={() => toggleTag(tagOption.id)}
                            bg={isSelected ? GOLD_BORDER : "gray.100"}
                            color={isSelected ? "#1A1A1A" : "gray.700"}
                            cursor="pointer"
                            borderRadius="full"
                          >
                            <TagLabel>{getTranslatedField(tagOption.translations, "en")}</TagLabel>
                          </Tag>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                )}
              </Box>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt="16px">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Status")}</FormLabel>
                  <Select value={editableNews.status} onChange={(e) => setEditableNews({ ...editableNews, status: e.target.value })} {...fieldProps}>
                    <option value="DRAFT">{t("Draft")}</option>
                    <option value="PUBLISHED">{t("Published")}</option>
                    <option value="ARCHIVED">{t("Archived")}</option>
                  </Select>
                </FormControl>
                <Checkbox
                  mt="36px"
                  isChecked={!!editableNews.priority}
                  onChange={(e) => setEditableNews({ ...editableNews, priority: e.target.checked })}
                  colorScheme="brand"
                >
                  {t("Priority")}
                </Checkbox>
                <Checkbox
                  mt="36px"
                  isChecked={!!editableNews.breaking_news}
                  onChange={(e) => setEditableNews({ ...editableNews, breaking_news: e.target.checked })}
                  colorScheme="brand"
                >
                  {t("Breaking News")}
                </Checkbox>
              </SimpleGrid>

              {editableNews.priority && (
                <FormControl mt="16px" maxW={{ base: "full", md: "33%" }}>
                  <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Hero Rank")}</FormLabel>
                  <Input
                    type="number"
                    placeholder={t("Lowest number shows first (leave empty for newest first)")}
                    value={editableNews.heroRank ?? ""}
                    onChange={(e) => setEditableNews({ ...editableNews, heroRank: e.target.value })}
                    {...fieldProps}
                  />
                </FormControl>
              )}

              {editableNews.status === "PUBLISHED" && (
                <FormControl isInvalid={!!fieldErrors.publisherBy} mt="16px">
                  <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Published By")}</FormLabel>
                  <Input
                    placeholder={t("Name of the publisher")}
                    value={editableNews.publisherBy}
                    onChange={(e) => setEditableNews({ ...editableNews, publisherBy: e.target.value })}
                    {...fieldProps}
                  />
                  <FormErrorMessage>{fieldErrors.publisherBy}</FormErrorMessage>
                </FormControl>
              )}

              <NewsMediaManager
                newsId={editableNews.id}
                images={editableNews.newsimages}
                videos={editableNews.newsvideos}
                onChange={refreshEditableNews}
              />
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={handleEditClose}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {viewingNews && (
        <ViewNewsModal news={viewingNews} onClose={() => setViewingNews(null)} />
      )}

      <ConfirmDialog
        isOpen={!!deleteTargetId}
        title={t("Delete this news item?")}
        body={t("This will permanently delete the article and its images/videos. This action cannot be undone.")}
        confirmLabel={t("Delete")}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTargetId(null)}
      />
    </Card>
  );
};

export default ListNews;
